import { HttpClient } from '@angular/common/http';
import { inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TaskExecutionListItemView } from '@fsf/types';
import { BehaviorSubject, finalize, Observable } from 'rxjs';
import { EnvironmentToken } from '../../app.config';
import { UserOption } from '../models/defect.model';
import { Task, TaskExecutionStatus } from '../models/task.model';

export class TaskService {
  #http = inject(HttpClient);
  #apiUrl = inject(EnvironmentToken).apiUrl;
  #isLoading = signal<Set<unknown>>(new Set());

  #tasks = new BehaviorSubject<Task[]>([]);

  public tasks$ = this.#tasks.asObservable();
  public tasks = toSignal(this.tasks$, {
    initialValue: [],
  });

  #responsibleUserOptions = new BehaviorSubject<UserOption[]>([]);
  public responsibleUserOptions$ = this.#responsibleUserOptions.asObservable();
  public responsibleUserOptions = toSignal(this.responsibleUserOptions$, {
    initialValue: [],
  });

  getTasks() {
    this.#execute(
      this.#http.get<{ data: Task[] }>(`${this.#apiUrl}/api/tasks`),
    ).subscribe({
      next: (response: any) => {
        const tasks: Task[] = (response.data ?? response).map((t: Task) =>
          TaskService.flatten(t),
        );
        this.#tasks.next(tasks);
      },
      error: (e: any) => console.log(e),
    });
  }

  /**
   * Loads one row per execution. Each row is a task whose `current_execution`
   * is that row's execution, so a completed execution stays listed next to
   * its follow-up.
   */
  getExecutionRows() {
    this.#execute(
      this.#http.get<{ data: TaskExecutionListItemView[] }>(
        `${this.#apiUrl}/api/tasks/executions`,
      ),
    ).subscribe({
      next: (response) => {
        const rows = response.data.map((item) =>
          TaskService.flatten({
            ...(item.task as Task),
            current_execution: item.execution,
          }),
        );
        this.#tasks.next(rows);
      },
      error: (e: unknown) => console.log(e),
    });
  }

  /**
   * Lifts the current execution's fields onto the task row so the list
   * can sort and filter on them like on any other column.
   */
  static flatten(task: Task): Task {
    const current = task.current_execution;
    return {
      ...task,
      responsible_user_name: task.responsible_user?.name ?? null,
      status: current?.status ?? null,
      due_at: current?.due_at ?? null,
      missed_count: current?.missed_count ?? 0,
      is_overdue: current?.is_overdue ?? false,
    };
  }

  getTask(id: number) {
    return this.#execute(
      this.#http.get<{ data: Task }>(`${this.#apiUrl}/api/tasks/${id}`),
    );
  }

  createTask(formData: FormData) {
    return this.#execute(
      this.#http.post<{ data: Task }>(`${this.#apiUrl}/api/tasks`, formData),
    );
  }

  updateTask(formData: FormData, id: number) {
    return this.#execute(
      this.#http.post<{ data: Task }>(
        `${this.#apiUrl}/api/tasks/${id}`,
        formData,
      ),
    );
  }

  updateExecutionStatus(
    taskId: number,
    executionId: number,
    status: TaskExecutionStatus,
  ) {
    return this.#execute(
      this.#http.patch<{ data: Task }>(
        `${this.#apiUrl}/api/tasks/${taskId}/executions/${executionId}`,
        { status },
      ),
    );
  }

  deleteTask(id: number) {
    return this.#execute(this.#http.delete(`${this.#apiUrl}/api/tasks/${id}`));
  }

  deleteMedia(taskId: number, mediaId: number) {
    return this.#execute(
      this.#http.delete(`${this.#apiUrl}/api/tasks/${taskId}/media/${mediaId}`),
    );
  }

  addComment(taskId: number, comment: string) {
    return this.#execute(
      this.#http.post<{ data: Task }>(
        `${this.#apiUrl}/api/tasks/${taskId}/comments`,
        { comment },
      ),
    );
  }

  deleteComment(taskId: number, commentId: number) {
    return this.#execute(
      this.#http.delete(
        `${this.#apiUrl}/api/tasks/${taskId}/comments/${commentId}`,
      ),
    );
  }

  getResponsibleUsers() {
    this.#execute(
      this.#http.get<{ data: UserOption[] }>(
        `${this.#apiUrl}/api/tasks/responsible-users`,
      ),
    ).subscribe({
      next: (response: any) => {
        this.#responsibleUserOptions.next(response.data ?? response);
      },
      error: (e: any) => console.log(e),
    });
  }

  #execute<T = unknown>(apiCall: Observable<T>) {
    this.#isLoading.update((x) => {
      x.add(apiCall);
      return new Set(x.values());
    });
    return apiCall.pipe(
      finalize(() =>
        this.#isLoading.update((x) => {
          x.delete(apiCall);
          return new Set(x.values());
        }),
      ),
    );
  }
}
